import { FrameData } from '../../types';
import { BeatPulse, SmoothedValue } from '../../utils/audioUtils';
import { hsl } from '../../utils/colorUtils';
import { LayerRenderer } from './LayerRenderer';

export class CircularBarsRenderer implements LayerRenderer {
  private beatPulse = new BeatPulse(0.91);
  private smoothRadius = new SmoothedValue(0.08);
  private rotation = 0;

  init(): void {}

  render(
    ctx: CanvasRenderingContext2D,
    frame: FrameData,
    settings: Record<string, number>,
    width: number,
    height: number,
  ): void {
    const { frequencyData, isBeat, beatIntensity, deltaTime } = frame;
    const barCount = Math.round(settings.barCount ?? 90);
    const baseRadius = settings.baseRadius ?? 0.2;
    const rotationSpeed = settings.rotationSpeed ?? 0.3;
    const colorHue = settings.colorHue ?? 180;
    const cx = width / 2;
    const cy = height / 2;
    const minDim = Math.min(width, height);

    this.beatPulse.update(isBeat, beatIntensity);

    const radiusPulse = this.smoothRadius.update(baseRadius + this.beatPulse.value * 0.06);
    const innerRadius = radiusPulse * minDim / 2;
    const maxBarLength = (minDim / 2) - innerRadius - 10;

    this.rotation += (rotationSpeed * deltaTime) / 1000;

    const usableBins = Math.floor(frequencyData.length * 0.5);
    const binsPerBar = usableBins / barCount;
    const angleStep = (Math.PI * 2) / barCount;

    ctx.lineWidth = Math.max(1, (Math.PI * 2 * innerRadius) / barCount * 0.6);
    ctx.lineCap = 'round';

    for (let i = 0; i < barCount; i++) {
      const startBin = Math.floor(i * binsPerBar);
      const endBin = Math.floor((i + 1) * binsPerBar);
      let sum = 0;
      for (let b = startBin; b < endBin; b++) sum += frequencyData[b];
      const amplitude = sum / (endBin - startBin) / 255;
      const barLength = amplitude * maxBarLength * 1.3;
      const angle = i * angleStep + this.rotation;

      const x1 = cx + Math.cos(angle) * innerRadius;
      const y1 = cy + Math.sin(angle) * innerRadius;
      const x2 = cx + Math.cos(angle) * (innerRadius + barLength);
      const y2 = cy + Math.sin(angle) * (innerRadius + barLength);

      const hue = (i / barCount) * 360 + colorHue;
      const lightness = 45 + this.beatPulse.value * 20;
      ctx.strokeStyle = hsl(hue, 80, lightness);

      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.stroke();
    }

    // Inner circle outline
    ctx.beginPath();
    ctx.arc(cx, cy, innerRadius, 0, Math.PI * 2);
    ctx.strokeStyle = hsl(colorHue, 60, 40, 0.5);
    ctx.lineWidth = 1;
    ctx.stroke();
  }

  resize(): void {}
  destroy(): void {}
}
